import {gql, DocumentNode} from "@apollo/client";
import {IManifestCardConfigurationsV2} from "../../components/manifest/card-configuration/v2/types/manifest-card-configurations.type";
import {
  DriverInformationValue,
  HoverOnDriversValues,
  HoverOnStopsValues,
  StopInformationValue
} from "../../components/manifest/card-configuration/v2/constants/enums";
import {buildFieldTree, treeToGraphQL} from "./dynamic-query-builder";

/**
 * Maps card configuration fields to GraphQL field paths
 */
const DRIVER_INFORMATION_FIELD_MAP: Record<string, string[]> = {
  [DriverInformationValue.DriverName]: ["driver.name"],
  [DriverInformationValue.DriverCode]: ["driver.code"],
  [DriverInformationValue.DriverCodeAndName]: ["driver.code", "driver.name"],
  [DriverInformationValue.DriverCapabilities]: ["driver.qualifications.id", "driver.qualifications.name"],
  [DriverInformationValue.DriverSkills]: ["driver.skills.id"]
};

const STOP_INFORMATION_FIELD_MAP: Record<string, string[]> = {
  [StopInformationValue.Address]: ["stops.address"],
  [StopInformationValue.AddressAndZone]: [
    "stops.address",
    "stops.city",
    "stops.state",
    "stops.zip",
    "stops.dispatchZone"
  ],
  [StopInformationValue.CityStateZone]: ["stops.city", "stops.state", "stops.dispatchZone"],
  [StopInformationValue.Zone]: ["stops.dispatchZone"],
  [StopInformationValue.ETATime]: ["stops.scheduledDateTime"],
  [StopInformationValue.CustomerName]: ["stops.order.customer.name"],
  [StopInformationValue.StopName]: ["stops.name"],
  [StopInformationValue.PhoneNumber]: ["stops.phone"],
  [StopInformationValue.Alias]: ["stops.order.alias"],
  [StopInformationValue.TotalPieces]: ["stops.job.pieces"],
  [StopInformationValue.InvoiceReference]: ["stops.order.invoiceReference"],
  [StopInformationValue.ServiceType]: ["stops.order.service"],
  [StopInformationValue.JobNumber]: ["stops.job.jobNumber"],
  [StopInformationValue.Auth]: ["stops.order.auth"],
  [StopInformationValue.RouteNumber]: ["stops.job.routeNumber"]
};

const HOVER_DRIVER_FIELD_MAP: Record<string, string[]> = {
  [HoverOnDriversValues.DRIVER_PHONE]: ["driver.phone"],
  [HoverOnDriversValues.ALIAS]: ["driver.alias"],
  [HoverOnDriversValues.MANIFEST_ID]: ["manifestDriverId"],
  [HoverOnDriversValues.MANIFEST_DATE]: ["manifestDate"],
  [HoverOnDriversValues.LAST_ZONE]: ["lastZone"],
  [HoverOnDriversValues.NOTES]: ["driver.note"],
  [HoverOnDriversValues.END_ZIP]: ["endZip"],
  [HoverOnDriversValues.DRIVER_CODE]: ["driver.code"],
  [HoverOnDriversValues.DRIVER_NAME]: ["driver.name"],
  [HoverOnDriversValues.DRIVER_CAPABILITIES]: ["driver.qualifications.id", "driver.qualifications.name"]
};

const HOVER_STOPS_FIELD_MAP: Record<string, string[]> = {
  [HoverOnStopsValues.STOP_ADDRESS]: ["stops.address", "stops.city", "stops.state", "stops.zip"],
  [HoverOnStopsValues.CUSTOMER]: ["stops.order.customer.name"],
  [HoverOnStopsValues.STOP_SEE]: ["stops.see"],
  [HoverOnStopsValues.STOP_PHONE]: ["stops.phone"],
  [HoverOnStopsValues.ROUTE_NUMBER]: ["stops.job.routeNumber"],
  [HoverOnStopsValues.SERVICE]: ["stops.order.service"],
  [HoverOnStopsValues.STOP_NAME]: ["stops.name"],
  [HoverOnStopsValues.STOP_NOTES]: ["stops.note"],
  [HoverOnStopsValues.PIECES_OR_WEIGHT]: ["stops.job.pieces", "stops.job.weight"],
  [HoverOnStopsValues.STOP_ROOM]: ["stops.room"],
  [HoverOnStopsValues.DESCRIPTION]: ["stops.order.description"],
  [HoverOnStopsValues.CALLER_AND_PHONE]: ["stops.order.caller", "stops.order.callerPhone"],
  [HoverOnStopsValues.AUTH]: ["stops.order.auth"],
  [HoverOnStopsValues.ALIAS]: ["stops.order.alias"],
  [HoverOnStopsValues.JOB_ALIAS]: ["stops.job.alias"],
  [HoverOnStopsValues.COD_STATUS_AND_AMOUNT]: ["stops.order.codAmount", "stops.order.codStatus"],
  [HoverOnStopsValues.OTHER_REFERENCE]: ["stops.order.otherReference"],
  [HoverOnStopsValues.INVOICE_REFERENCE]: ["stops.order.invoiceReference"],
  [HoverOnStopsValues.JOB_NUMBER]: ["stops.job.jobNumber"]
};

/**
 * Base fields that are always required for manifest operations
 */
const BASE_FIELDS = new Set([
  "manifestDriverId",
  "manifestStatus",
  "vehicleType.vehicleTypeId",
  "vehicleType.description",
  "driver.driverId",
  "driver.code",
  "driver.name",
  "driver.skills.id",
  "driver.qualifications.id",
  "driver.qualifications.name",
  "stops.jobStopId",
  "stops.address",
  "stops.city",
  "stops.state",
  "stops.zip",
  "stops.timeZone",
  "stops.manifestSequence",
  "stops.scheduledDateTime",
  "stops.earlyDateTime",
  "stops.lateDateTime",
  "stops.arriveDateTime",
  "stops.departDateTime",
  "stops.podDateTime",
  "stops.stopType",
  "stops.jobStopStatus",
  "stops.job.site.siteId",
  "stops.order.customer.customerId",
  "stops.order.priority.orderPriorityId",
  "stops.order.service",
  "stops.indicatorColor",
  "stops.indicatorRank",
  "stops.indicatorExpiry",
  "stops.indicatorSnoozeUntil",
  "stopCount",
  // The replication Lambda leaves stops out once a manifest passes the tenant's cap and explains
  // itself in this alert. Without it the cards cannot tell an over-cap manifest from an empty one.
  "metadata.alerts.level",
  "metadata.alerts.message"
]);

/**
 * Extracts required GraphQL field paths from card configuration
 */
export function extractRequiredFieldsFromConfig(
  config: IManifestCardConfigurationsV2 | null,
  additionalFields?: string[]
): Set<string> {
  const fields = new Set<string>(BASE_FIELDS);
  if (additionalFields) {
    additionalFields.forEach((f) => fields.add(f));
  }

  if (!config) {
    return fields;
  }

  // Extract fields from driverInformation
  config.driverInformation?.forEach((driverInfo) => {
    const mappedFields = DRIVER_INFORMATION_FIELD_MAP[driverInfo];
    if (mappedFields) {
      mappedFields.forEach((field) => fields.add(field));
    }
  });

  // Extract fields from stopInformation
  config.stopInformation?.forEach((stopInfo) => {
    const mappedFields = STOP_INFORMATION_FIELD_MAP[stopInfo.field];
    if (mappedFields) {
      mappedFields.forEach((field) => fields.add(field));
    }
  });

  // Extract fields from hoverDriverConfig
  config.hoverDriverConfig?.forEach((option) => {
    const mappedFields = HOVER_DRIVER_FIELD_MAP[option.value];
    if (mappedFields) {
      mappedFields.forEach((field) => fields.add(field));
    }
  });

  // Extract fields from hoverStopsConfig
  config.hoverStopsConfig?.forEach((option) => {
    const mappedFields = HOVER_STOPS_FIELD_MAP[option.value];
    if (mappedFields) {
      mappedFields.forEach((field) => fields.add(field));
    }
  });

  return fields;
}

/**
 * Builds a dynamic GraphQL query based on card configuration
 */
export function buildManifestQuery(
  config: IManifestCardConfigurationsV2 | null,
  additionalFields?: string[]
): DocumentNode {
  const requiredFields = extractRequiredFieldsFromConfig(config, additionalFields);
  const fieldTree = buildFieldTree(requiredFields);
  const fieldsGraphQL = treeToGraphQL(fieldTree);

  const query = gql`
    query SearchManifests($filter: ManifestFilter, $sort: [ManifestSortInput], $offset: Int, $limit: Int) {
      searchManifests(filter: $filter, sort: $sort, offset: $offset, limit: $limit) {
        items {
          ${fieldsGraphQL}
        }
        nextToken
        total
      }
    }
  `;

  return query;
}
// test
